import React, { useContext, useEffect, useState } from 'react';
import { Loan, Task, TaskResponse, TaskState } from 'gql/graphql';
import { CardLoanTasksProps } from '../../../../../components/Atoms/Cards/CardLoanTasks/CardLoanTasks';
import { ModuleKeys } from '../../../../../services/task/modules-keys.constanst';
import { useGetUserLoggin } from '../../../../../services/user/querys';
import DscrQuestionsAgreements from './DscrQuestionsAgreements';
import CircleTag from '../../../../../components/Atoms/Tags/CircleTag/CircleTag';

interface Props {
  tasks?: Task[] | any[];
  loan?: Loan | any;
  trackRecord: any;
}

const useCardDSCRQuestionAgreements = ({ tasks, loan, trackRecord }: Props) => {

  const { data: user } = useGetUserLoggin();
  const [dataTask, setDataTask] = useState<Task>();
  const [stepNow, setStepNow] = useState<TaskResponse>();

  const isDisabled = trackRecord?.state === TaskState.Draft;

  const getTotalApproved = () => {
    const responseLeaseAgreements = dataTask?.responses?.find((item: any) => item.key === 'lease-agreements');
    return responseLeaseAgreements?.files?.filter((item: any) => item.state !== 'request_error').length || 0;
  }

  useEffect(() => {
    if (tasks) {
      const data: Task = tasks.find((item) => item.key === ModuleKeys.DSCR);

      setDataTask(data);
      // setResponse(data?.responses?.find(item => item.metadata.userEmail));

      if (data?.responses.length > 0)
        // Based on the responses the step is set
        setStepNow(data?.responses.reduce((max, obj) => obj.step > max.step ? obj : max, data?.responses[0]));
    }
  }, [tasks, dataTask?.responses]);

  const getStateResponse = () => {
    if (dataTask?.state === TaskState.Approved && (dataTask?.responses?.find((Item: any) =>
      Item.key === 'lease-agreements')?.files?.length ?? 0) > 0) {
      return (dataTask?.responses?.find((Item: any) =>
        Item.key === 'lease-agreements')?.files?.filter((item: any) => item.state === 'request_error')?.length ?? 0) > 0 ?
        dataTask?.responses?.find((Item: any) =>
          Item.key === 'lease-agreements')?.files?.filter((item: any) => item.state === 'approved').length === dataTask.metadata?.ammountFiles ?
          TaskState.Approved : TaskState.RequestError : TaskState.Approved;
    } else if (dataTask?.state === TaskState.Review) return TaskState.Review;
    else if (dataTask?.state === TaskState.RequestError) return TaskState.RequestError;
    else if (dataTask?.state === TaskState.Incomplete) return TaskState.Incomplete;
    else if (dataTask?.state === TaskState.Approved) {
      const totalUnits = dataTask?.metadata?.totalUnits || 0;
      const vacantUnits = dataTask?.metadata?.vacantUnits || 0;
      const occupiedUnits = totalUnits - vacantUnits;
      // Si no hay occupied units, está correctamente Approved (no había archivos que subir)
      if (occupiedUnits === 0) {
        return TaskState.Approved;
      }
      return TaskState.Incomplete;
    }
    return TaskState.Draft;
  }

  const generateCirclesFooter: any = () => {
    const totalUnits = dataTask?.metadata?.totalUnits || 0;
    const vacantUnits = dataTask?.metadata?.vacantUnits || 0;
    const occupiedUnits = totalUnits - vacantUnits;
    const responseLeaseAgreements = dataTask?.responses?.find((item: any) => item.key === 'lease-agreements');
    const files = responseLeaseAgreements?.files || [];
    const state = responseLeaseAgreements?.state;

    const validFiles = files.filter((item: any) => item.state !== 'request_error');
    const rejectedFiles = files.filter((item: any) => item.state === 'request_error');
    const neededRejected = Math.max(0, occupiedUnits - validFiles.length);
    const currentFilesForError = [...validFiles, ...rejectedFiles.slice(-neededRejected)];

    return Array.from({ length: occupiedUnits }).map((_, index) => {
      let leaseState = 'draft';
      if (!responseLeaseAgreements || !files.length) {
        leaseState = 'draft';
      } else if (state === TaskState.Review || state === TaskState.Approved) {
        leaseState = validFiles[index]?.state || 'draft';
      } else if (state === TaskState.RequestError) {
        leaseState = currentFilesForError[index]?.state || 'draft';
      } else {
        leaseState = files[index]?.state || 'draft';
      }
      return { state: leaseState };
    });
  };

  const card: CardLoanTasksProps =
  {
    key: ModuleKeys.DSCR,
    title: <h4 className="h4">Rents and lease agreements</h4>,
    steps: 2,
    stepCurrent: stepNow?.step || 0,
    type: 'quick task',
    state: getStateResponse(),
    // stateRename: { REVIEW: 'AWAITING ACTION' },
    actions: dataTask && user ? [<DscrQuestionsAgreements key={1} task={dataTask} currentStep={(() => {
      // No responses + REQUEST_ERROR → open at step 2 to show SectionErrorTask
      if (!stepNow) return dataTask?.state === TaskState.RequestError ? 2 : undefined;
      // Step 1 completed (APPROVED) → advance to step 2
      if (stepNow.step === 1 && stepNow.state === TaskState.Approved) return 2;
      return stepNow.step;
    })()} stateTask={getStateResponse()} />] : [],
    text: (
      <div>
        <p className="body-card">
          My building has  <span className='body-card-bold-line'>
            {dataTask?.metadata?.totalUnits ? dataTask?.metadata?.totalUnits : 'unknown'}
          </span> unit(s). <span className='body-card-bold-line'>
            {dataTask?.metadata?.occupiedUnits ? dataTask?.metadata?.occupiedUnits : 'Some'}
          </span> occupied. <span className='body-card-bold-line'>
            {dataTask?.metadata?.vacantUnits ? dataTask?.metadata?.vacantUnits : '0'}
          </span> vacant.
          <span className={`body-card-bold-line body-card-bold-line-${getStateResponse()}`}>
          </span>
        </p>
      </div>
    ),
    text2: (
      <div className='docs-section'>
        <div className='labels'>
          <p className={`body-regular body-regular-bold ${isDisabled && 'disabled'}`}>
            Lease agreements for occupied units
          </p>
          <p className={`body-card ${isDisabled && 'disabled'}`}>
            {getTotalApproved()}/{generateCirclesFooter()?.length || 0} submitted
          </p>
        </div>
        <div className={`circles-tags ${isDisabled && 'disabled'}`}>
          {generateCirclesFooter().map((item: { state: string }, index: number) => {
            let _state = item.state?.toUpperCase() || 'DRAFT';

            return (<CircleTag
              key={index}
              state={_state}
              tagKey={_state}
              isIconState={true}
            />
            );
          })}
        </div>
      </div>
    )
  };

  if (!dataTask)
    return { card: null };

  return {
    card,
    dataTask
  };
};

export default useCardDSCRQuestionAgreements;